import { BaseExercise, Exercise, MainExercise } from "@domain/exercise/exercise";
import { LibraryFilteringState } from "@domain/library/library-filter-manager";
import { libraryFilterManager } from "@core/service-container";
import { OnboardingProgramQuickstartStep } from "@domain/onboarding/onboarding";
import { OnboardingManager } from "@domain/onboarding/onboarding-manager";
import { ProgramContent, getDayColumnIndexes, updateExerciseFromProgram } from "@domain/program/program";
import { DayIndex, ProgramDays } from "@domain/program/program-days";
import { Session } from "@domain/program/session";
import {
	AlternativeErrorType,
	MAX_ALTERNATIVE_EXERCISES,
} from "@ui/pages/program-builder/alternative-exercise/alternative-exercises-utils";
import { immutableInsert, move, reorder } from "@utils/array-utils";
import { isNil, isNotNil } from "@utils/assert";
import { Dayjs } from "dayjs";
import _ from "lodash";
import { Observable, WritableObservable, observable } from "micro-observables";
import { complementaryGuide3ForceVisible, hideComplementaryGuide3 } from "@ui/pages/program-builder/quickstart/complementary-tooltip-signal";
import { v4 as uuid } from "uuid";

export class ProgramContentEditor {
	private readonly _editedProgram: WritableObservable<ProgramContent>;
	private readonly _dataIsLoading: WritableObservable<boolean>;
	private readonly _alternativeError = observable<AlternativeErrorType | null>(null);
	firstExerciseDraggableId: Observable<string | undefined>;

	editedProgram: Observable<ProgramContent>;
	dataIsLoading: Observable<boolean>;
	alternativeError = this._alternativeError.readOnly();
	programMetadata: Observable<{ exerciseCount: number; alternativeExerciseCount: number }>;

	constructor(
		programContent: ProgramContent,
		private readonly onboardingManager: OnboardingManager,
		dataIsLoading = false,
	) {
		this._editedProgram = observable(programContent);
		this.editedProgram = this._editedProgram.readOnly();
		this._dataIsLoading = observable(dataIsLoading);
		this.dataIsLoading = this._dataIsLoading.readOnly();

		this.firstExerciseDraggableId = this._editedProgram.select(program =>
			this.getFirstExerciseDraggableId(program.days),
		);

		this.programMetadata = this._editedProgram.select(program => {
			let exerciseCount = 0;
			let alternativeExerciseCount = 0;

			program.days.forEach(([, session]: [DayIndex, Session]) => {
				exerciseCount += session.exercises.length;
				session.exercises.forEach((exercise: MainExercise) => {
					alternativeExerciseCount += exercise.alternativeExercises.length;
				});
			});

			return { exerciseCount, alternativeExerciseCount };
		});
	}

	async reorderSessionExercises(
		sessionIndex: DayIndex,
		sourceExerciseIndex: number,
		destinationExerciseIndex: number,
	): Promise<void> {
		const session = this._editedProgram.get().days.get(sessionIndex);
		const movedExercise = session.exercises[sourceExerciseIndex];
		const newSession = {
			exercises: reorder(session.exercises, sourceExerciseIndex, destinationExerciseIndex),
		};

		this.hideComplementaryGuideIfNeeded(movedExercise.draggableId);
		this.editSession(sessionIndex, newSession);
	}

	async addExerciseToSession(
		exerciseToCopy: BaseExercise,
		sessionIndex: DayIndex,
		exerciseIndex: number,
		skipOnboardings = false,
	): Promise<void> {
		const session = this._editedProgram.get().days.get(sessionIndex);
		const newExercise = this.createMainExerciseFromBaseExercise(exerciseToCopy);
		const newSession = {
			exercises: immutableInsert(newExercise, session.exercises, exerciseIndex),
		};
		await this.editSession(sessionIndex, newSession);

		libraryFilterManager.setLastAddedExerciseMetadata(newExercise.draggableId);

		if (libraryFilterManager.filteringState.get() === LibraryFilteringState.COMPLEMENTARY && !skipOnboardings) {
			await this.onboardingManager.passStepOnboarding(
				OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_03,
			);
		}
	}

	duplicateExercise(sessionIndex: DayIndex, exerciseIndex: number): void {
		const session = this._editedProgram.get().days.get(sessionIndex);
		const newSession = {
			exercises: immutableInsert(
				session.exercises[exerciseIndex],
				session.exercises,
				exerciseIndex + 1,
				this.createExerciseCopy,
			),
		};
		this.editSession(sessionIndex, newSession);
	}

	duplicateSession(sourceSessionIndex: DayIndex, destinationSessionIndex: DayIndex): void {
		const sourceSession = this._editedProgram.get().days.get(sourceSessionIndex);
		const newSession = this.createSessionCopy(sourceSession);
		this.editSession(destinationSessionIndex, newSession);
	}

	clearSession(sessionIndex: DayIndex): void {
		const emptySession: Session = { exercises: [] };
		this.editSession(sessionIndex, emptySession);
	}

	deleteExercise(sessionIndex: DayIndex, exerciseIndex: number): void {
		const session = this._editedProgram.get().days.get(sessionIndex);
		const exercises = Array.from(session.exercises);
		exercises.splice(exerciseIndex, 1);
		const newSession = {
			exercises,
		};
		this.editSession(sessionIndex, newSession);
	}

	moveExercise(
		sourceSessionIndex: DayIndex,
		sourceExerciseIndex: number,
		destinationSessionIndex: DayIndex,
		destinationExerciseIndex: number,
	): void {
		const editedProgram = this._editedProgram.get();
		const sourceSession = editedProgram.days.get(sourceSessionIndex);
		const movedExercise = sourceSession.exercises[sourceExerciseIndex];
		const destinationSession = editedProgram.days.get(destinationSessionIndex);
		const { newSource, newDestination } = move(
			sourceSession.exercises,
			destinationSession.exercises,
			sourceExerciseIndex,
			destinationExerciseIndex,
		);
		const newProgram = {
			...editedProgram,
			days: new ProgramDays(
				editedProgram.days.map(([dayIndex, session]: [DayIndex, Session]) => {
					if (dayIndex === sourceSessionIndex) {
						return [dayIndex, { exercises: newSource }] as const;
					}
					if (dayIndex === destinationSessionIndex) {
						return [dayIndex, { exercises: newDestination }] as const;
					}
					return [dayIndex, session] as const;
				}),
				),
			};
		this.hideComplementaryGuideIfNeeded(movedExercise.draggableId);
		this.editProgram(newProgram);
	}

	async editProgram(newProgramContent: ProgramContent): Promise<void> {
		this._editedProgram.set(newProgramContent);
	}

	renameProgram(newName: string): void {
		this._editedProgram.set({ ...this._editedProgram.get(), name: newName });
	}

	editProgramDates(startDate?: Dayjs, endDate?: Dayjs): void {
		this._editedProgram.set({ ...this._editedProgram.get(), startDate, endDate });
	}

	async editSession(sessionIndex: DayIndex, newSession: Session): Promise<void> {
		const editedProgram = this._editedProgram.get();
		const newProgram = {
			...editedProgram,
			days: new ProgramDays(
				editedProgram.days.map(([dayIndex, session]: [DayIndex, Session]) => [
					dayIndex,
					dayIndex === sessionIndex ? newSession : session,
				]),
			),
		};

		await this.editProgram(newProgram);
	}

	editExercise(exerciseDraggableId: string, newExercise: MainExercise): void {
		const editedProgram = this._editedProgram.get();
		this.editProgram(updateExerciseFromProgram(editedProgram, exerciseDraggableId, newExercise));
	}

	async addLibraryExerciseToAlternatives(exercise: BaseExercise, alternativeExerciseIndex: number): Promise<void> {
		const editedExerciseWithAlternatives = libraryFilterManager.exerciseWithAlternativesEdited.get();
		const newAlternative = this.createExerciseCopy(exercise);

		if (
			isNil(editedExerciseWithAlternatives) ||
			!this.isNewAlternativeValid(editedExerciseWithAlternatives, newAlternative)
		) {
			return;
		}

		libraryFilterManager.exerciseWithAlternativesEdited.set({
			...editedExerciseWithAlternatives,
			alternativeExercises: immutableInsert(
				newAlternative,
				editedExerciseWithAlternatives.alternativeExercises,
				alternativeExerciseIndex,
			),
		});

		libraryFilterManager.setLastAddedExerciseMetadata(newAlternative.draggableId, true);
		// Alternative onboarding steps removed: no onboarding step to pass here
	}

	reorderAlternativeExercises(
		sourceAlternativeExerciseIndex: number,
		destinationAlternativeExerciseIndex: number,
	): void {
		libraryFilterManager.exerciseWithAlternativesEdited.update((exercise: MainExercise | null) =>
			isNotNil(exercise)
				? {
						...exercise,
						alternativeExercises: reorder(
							exercise.alternativeExercises,
							sourceAlternativeExerciseIndex,
							destinationAlternativeExerciseIndex,
						),
					}
				: null,
		);
	}

	updateAlternativeFromEditedExercise(exercise: BaseExercise): void {
		const editedExercise = libraryFilterManager.exerciseWithAlternativesEdited.get();

		if (isNil(editedExercise)) {
			return;
		}

		libraryFilterManager.exerciseWithAlternativesEdited.set({
			...editedExercise,
			alternativeExercises: editedExercise.alternativeExercises.map((alternative: BaseExercise) =>
				alternative.draggableId === exercise.draggableId ? exercise : alternative,
			),
		});
	}

	removeAlternativeFromExercise(alternative: BaseExercise): void {
		libraryFilterManager.exerciseWithAlternativesEdited.update((exercise: MainExercise | null) =>
			isNotNil(exercise)
				? {
						...exercise,
						alternativeExercises: exercise.alternativeExercises.filter((a: BaseExercise) => a.id !== alternative.id),
					}
				: null,
		);
	}

	getExerciseIndex(exerciseDraggableId: string): { sessionIndex: DayIndex; exerciseIndex: number } | null {
		const editedProgram = this._editedProgram.get();
		for (const [dayIndex, session] of editedProgram.days.entries as Iterable<[DayIndex, Session]>) {
			const exerciseIndex = session.exercises.findIndex((ex: Exercise) => ex.draggableId === exerciseDraggableId);
			if (exerciseIndex > -1) {
				return { sessionIndex: dayIndex, exerciseIndex };
			}
		}
		return null;
	}

	getExerciseCount(): number {
		const editedProgram = this._editedProgram.get();
		let exerciseCount = 0;
		editedProgram.days.forEach(([, session]: [DayIndex, Session]) => {
			exerciseCount += session.exercises.length;
		});
		return exerciseCount;
	}

	getExerciseCountInSession(sessionIndex: DayIndex): number {
		const editedProgram = this._editedProgram.get();
		return editedProgram.days.get(sessionIndex).exercises.length;
	}

	getDaySessions(sessionIndex: DayIndex): Exercise[] {
		const editedProgram = this._editedProgram.get();
		return editedProgram.days.get(sessionIndex).exercises;
	}

	getAlternativeExerciseCount(): number {
		const editedProgram = this._editedProgram.get();
		let alternativeExerciseCount = 0;

		editedProgram.days.forEach(([, session]: [DayIndex, Session]) => {
			session.exercises.forEach((exercise: MainExercise) => {
				alternativeExerciseCount += exercise.alternativeExercises.length;
			});
		});
		return alternativeExerciseCount;
	}

	setDataIsLoading(isLoading: boolean): void {
		this._dataIsLoading.set(isLoading);
	}

	setExerciseWithAlternativesEdited(exercise: MainExercise | null): void {
		libraryFilterManager.exerciseWithAlternativesEdited.set(exercise);
	}

	applyChangesAndResetEditedExercise(): void {
		const editedExercise = libraryFilterManager.exerciseWithAlternativesEdited.get();
		if (isNil(editedExercise)) {
			return;
		}
		this.editExercise(editedExercise.draggableId, editedExercise);
		libraryFilterManager.exerciseWithAlternativesEdited.set(null);
		this.cleanAlternativeExerciseError();
	}

	cleanAlternativeExerciseError(): void {
		this._alternativeError.set(null);
	}

	private createExerciseCopy<T extends Exercise>(exercise: T): T {
		return { ...exercise, draggableId: uuid() };
	}

	private createMainExerciseFromBaseExercise(baseExercise: BaseExercise): MainExercise {
		return { ...this.createExerciseCopy(baseExercise), alternativeExercises: [] };
	}

	private createSessionCopy(session: Session): Session {
		return {
			exercises: session.exercises.map(exercise => this.createExerciseCopy(exercise)),
		};
	}

	private hideComplementaryGuideIfNeeded(movedExerciseDraggableId: string): void {
		const lastAdded = libraryFilterManager.lastAddedExerciseMetadata.get();
		if (
			libraryFilterManager.filteringState.get() === LibraryFilteringState.COMPLEMENTARY &&
			complementaryGuide3ForceVisible.get() &&
			lastAdded?.isComplementary &&
			lastAdded.draggableId === movedExerciseDraggableId
		) {
			hideComplementaryGuide3();
		}
	}

	private isNewAlternativeValid(exercise: MainExercise, newAlternativeExercise: BaseExercise): boolean {
		if (exercise.alternativeExercises.length === MAX_ALTERNATIVE_EXERCISES) {
			this._alternativeError.set(AlternativeErrorType.MaxExercisesReached);
			return false;
		} else if ([exercise, ...exercise.alternativeExercises].find(a => a.id === newAlternativeExercise.id)) {
			this._alternativeError.set(AlternativeErrorType.ThisAlreadyExists);
			return false;
		}
		return true;
	}

	private getFirstExerciseDraggableId(programDays: ProgramDays): string | undefined {
		let firstExerciseId: string | undefined;
		const reorderedDayIndexes = getDayColumnIndexes();
		reorderedDayIndexes.some(dayIndex => {
			const session = programDays.get(dayIndex);
			if (!_.isEmpty(session.exercises)) {
				firstExerciseId = session.exercises[0].draggableId;
				return true;
			}
			return false;
		});
		return firstExerciseId;
	}
}
